import React from 'react';
import CovidEndpage from './CovidEndpage';
import RebrandExistingUser from './RebrandExistingUser';
import RebrandEpForm from './RebrandEpForm';
import StandardEndpage from './StandardEndpage';
import EmbeddedNonBranded from './EmbeddedNonBranded';
import BuildWithAI from './BuildWithAI';
import Embedded from './Embedded';
import Research from './Research';
import NonBranded from './NonBranded';
import RebrandEpFormIntl from './RebrandEpFormIntl';
import { EndpageVariant } from '~helpers/pages/types';
import PollingEndpage from './StandardEndpage/PollingEndpage';
import DareToAsk from '~app/components/endpages/DareToAsk';
import OptionalPaths from '~app/components/endpages/OptionalPaths';
import OptionalMobilePaths from '~app/components/endpages/OptionalMobilePaths';
import UserResearchEndpage from '~app/components/endpages/UserResearch';
import OptionalPathsV2 from '~app/components/endpages/OptionalPathsV2';
import PersonalizationEndPage from '~app/components/endpages/Personalization';
import UpdatedExistingUser from './UpdatedExistingUser';

export type WeightedElement = {
  page: EndpageVariant;
  weight: number;
  surveyId?: string;
};

export type PollingRegionEndpagesList = {
  region: string;
  templates: WeightedElement[];
};

export type PollingCountryEndpageList = {
  country: string;
  regions: PollingRegionEndpagesList[];
  templates: WeightedElement[];
};

export const GUAC_EXISTING_USER_PAGES: WeightedElement[] = [{ page: 'rebrandExistingUser', weight: 1.0 }];

export const NON_GUAC_EXISTING_USER_PAGES: WeightedElement[] = [{ page: 'rebrandExistingUser', weight: 1.0 }];

export const US_GEO_PAGES: WeightedElement[] = [
  { page: 'covidEndpage', weight: 0.3 },
  { page: 'rebrandEpForm', weight: 0.7 },
];

export const GAC_GEO_PAGES: WeightedElement[] = [{ page: 'rebrandEpForm', weight: 1.0 }];

export const NON_GUAC_GEO_PAGES: WeightedElement[] = [{ page: 'rebrandEpForm', weight: 1.0 }];

export const NON_ENGLISH_PAGES: WeightedElement[] = [{ page: 'rebrandEpFormIntl', weight: 1.0 }];

export const endpageVariants: { [key: string]: React.ReactElement } = {
  pollingUsMap: <PollingEndpage pageName="PollingUSMap" utSource="survey-thanks" />,
  pollingUkMap: <PollingEndpage pageName="PollingUKMap" utSource="survey-thanks" />,
  covidNearYouEndpage: <PollingEndpage pageName="CovidNearYouEndpage" utSource="survey-thanks" />,
  pollingCanadaMap: <PollingEndpage pageName="PollingCanadaMap" utSource="survey-thanks" />,
  cxEndpage: <StandardEndpage pageName="CxEndpage" utSource="survey-thanks" />,
  racialJusticeEndpage: <StandardEndpage pageName="RacialJusticeEndpage" utSource="survey-thanks" />,
  corporateSignatureResearchEndpage: (
    <StandardEndpage pageName="CorporateSignatureResearchEndpage" utSource="survey-thanks" />
  ),
  mrxEndpage: <StandardEndpage pageName="MrxEndpage" utSource="survey-thanks" />,
  giancarloEndpage: <StandardEndpage pageName="GiancarloEndpage" utSource="survey-thanks" />,
  rebrandEpForm: <RebrandEpForm />,
  rebrandEpFormV1: <RebrandEpForm experiment utSource2="RebrandEpFormV1" />,
  covidEndpage: <CovidEndpage />,
  rebrandExistingUser: <RebrandExistingUser />,
  embeddedNonBranded: <EmbeddedNonBranded />,
  embedded: <Embedded />,
  research: <Research />,
  nonBranded: <NonBranded />,
  rebrandEpFormIntl: <RebrandEpFormIntl />,
  epFormBrazil: <RebrandEpFormIntl treatment="treatment1" />,

  dareToAskPractical: (
    <DareToAsk
      utSource2="DareToAskPractical"
      splitHeroDarkBackground
      splitHeroCTA
      splitHeroPrimaryCTA="Explore the trends"
      splitHeroSecondaryCTA="Sign up free"
      splitHeroImage="dareToAskUS"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
    />
  ),
  dareToAskPracticalUk: (
    <DareToAsk
      utSource2="DareToAskPracticalUk"
      splitHeroDarkBackground
      splitHeroCTA
      splitHeroPrimaryCTA="Explore the trends"
      splitHeroSecondaryCTA="Sign up free"
      splitHeroImage="dareToAskUK"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
    />
  ),
  dareToAskV3SignUp: (
    <DareToAsk
      utSource2="DareToAskV3SignUp"
      splitHeroDarkBackground
      splitHeroCTA
      splitHeroPrimaryCTA="Sign up free"
      splitHeroSecondaryCTA="Explore the trends"
      splitHeroImage="dareToAskUS"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
    />
  ),
  dareToAskCreate: (
    <DareToAsk
      utSource2="DareToAskCreate"
      splitHeroDarkBackground
      splitHeroCTA
      splitHeroPrimaryCTA="Create a Survey"
      splitHeroSecondaryCTA="Explore the trends"
      splitHeroImage="dareToAskUS"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
    />
  ),
  bwaiGIFInsight: (
    <DareToAsk
      utSource2="BwAiGIFInsight"
      splitHeroImage="bwaiGIFTest"
      splitHeroCTA
      splitHeroDarkBackground
      splitHeroPrimaryCTA="Try Build with AI"
      splitHeroSecondaryCTA="Or create a survey another way"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
      buildWithAI
    />
  ),
  bwaiGIFCreation: (
    <DareToAsk
      utSource2="BwAiGIFCreation"
      splitHeroImage="bwaiGIFTest"
      splitHeroCTA
      splitHeroDarkBackground
      splitHeroPrimaryCTA="Try Build with AI"
      splitHeroSecondaryCTA="Or create a survey another way"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
      buildWithAI
    />
  ),
  curiosityConEp: (
    <DareToAsk
      utSource2="curiosityConEp"
      splitHeroImage=""
      splitHeroDarkBackground
      splitHeroCTA
      splitHeroPrimaryCTA="RSVP for free"
      splitHeroSecondaryCTA="Create a survey"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
      curiosityCon
    />
  ),
  rebrandEP2025: (
    <DareToAsk
      utSource2="rebrandEP2025"
      splitHeroDarkBackground
      splitHeroCTA
      splitHeroPrimaryCTA="Learn more"
      splitHeroSecondaryCTA="Sign up free"
      splitHeroImage="rebrandEP2025"
      fortuneBannerDarkBackground
      fortuneBannerLogoWall
      rebrandEP2025
    />
  ),
  endpageBuildWithAIT1: <BuildWithAI treatment="treatment1" />,
  endpageBuildWithAIT2: <BuildWithAI treatment="treatment2" />,
  optionalPath1: <OptionalPaths utSource2="OptionalPathDesktop1" />,
  optionalPath2: <OptionalPaths utSource2="OptionalPathDesktop2" />,
  optionalPath3: <OptionalPaths utSource2="OptionalPathDesktop3" />,
  optionalPathOriginal: <OptionalPaths utSource2="optionalPathOriginal" />,
  optionalPathUpdated: <OptionalPathsV2 utSource2="optionalPathUpdated" />,
  optionalPathsMWeb1: <OptionalMobilePaths utSource2="optionalPathsMWeb1" />,
  optionalPathsMWeb2: <OptionalMobilePaths utSource2="optionalPathsMWeb2" />,
  optionalPathsMApp1: <OptionalMobilePaths utSource2="optionalPathsMApp1" />,
  optionalPathsMApp2: <OptionalMobilePaths utSource2="optionalPathsMApp2" />,
  userResearchStandard: <UserResearchEndpage utSource2="userResearchStandard" />,
  userResearchStyled: <UserResearchEndpage utSource2="userResearchStyled" />,
  userResearchMobileStyle: <UserResearchEndpage utSource2="userResearchMobileStyle" />,
  userResearch4: <UserResearchEndpage utSource2="userResearch4" />,

  updatedExistingUser: <UpdatedExistingUser utSource2="updatedExistingUser" />,
  popularTemplates: (
    <PersonalizationEndPage treatment="treatment1" utSource="survey-thanks" utSource2="popularTemplates" />
  ),
  employeeFeedbackTemplates: (
    <PersonalizationEndPage treatment="treatment2" utSource="survey-thanks" utSource2="employeeFeedbackTemplates" />
  ),
};
